import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Crown, TrendingDown, TrendingUp } from "lucide-react";

import type { TopItemRow } from "./types";

type TopPerformersProps = {
  items: TopItemRow[];
  title?: string;
  description?: string;
};

export function TopPerformers({
  items,
  title,
  description,
}: TopPerformersProps) {
  const { t } = useTranslation("dashboard");
  const heading = title ?? t("topPerformers.title");
  const subheading = description ?? t("topPerformers.description");
  return (
    <Card className="border border-gray-200/60 bg-white/95 shadow-lg dark:border-gray-700/60 dark:bg-gray-900">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
          <Crown className="h-4 w-4 text-yellow-500" />
          {heading}
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground">{subheading}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200/70 bg-gray-50/70 p-6 text-center text-sm text-muted-foreground dark:border-gray-700/60 dark:bg-gray-800/60">
            {t("topPerformers.empty")}
          </div>
        ) : (
          items.slice(0, 5).map((item, index) => (
            <div key={item.id ?? index} className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {item.name}
                  </span>
                  {index === 0 ? <Crown className="h-3 w-3 text-yellow-500" /> : null}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {item.trend === "up" ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : item.trend === "down" ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : null}
                    {item.trendLabel}
                  </Badge>
                  <span className="font-medium text-muted-foreground">{item.revenueLabel}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={item.percentage} className="h-1.5 flex-1" />
                <span className="w-14 text-right text-xs font-medium text-muted-foreground">
                  {item.quantity}
                </span>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}
